<section class="rounded-lg border !border-slate-200 bg-white p-4 shadow-sm">
  @if (writtenOff) {
    <div class="mb-3 flex items-start gap-2 rounded-lg bg-rose-50 px-3 py-2 text-xs leading-5 text-rose-700">
      <app-icon class="!h-4 !w-4 shrink-0 !text-[16px] !leading-none">lock</app-icon>
      {{ 'stock.card_items.locked_hint' | translate }}
    </div>
  }

  @if (items.length) {
    <div
      class="mb-1 hidden gap-3 border !border-transparent px-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400 xl:grid xl:grid-cols-[minmax(0,2.2fr)_minmax(0,1.4fr)_minmax(0,0.8fr)_minmax(0,1.3fr)_minmax(0,1.3fr)_minmax(0,1fr)_1.75rem]"
    >
      <span>{{ 'stock.product' | translate }}</span>
      <span>{{ 'warehouse' | translate }}</span>
      <span>{{ 'stock.quantity' | translate }}</span>
      <span>{{ 'price' | translate }}</span>
      <span>{{ 'discount' | translate }}</span>
      <span class="text-right">{{ 'amount' | translate }}</span>
      <span></span>
    </div>
  }

  <div class="flex flex-col gap-2">
    @for (item of items; track item.id) {
      <div
        class="grid grid-cols-1 gap-2 rounded-lg border p-2.5 transition xl:items-center xl:gap-3 xl:grid-cols-[minmax(0,2.2fr)_minmax(0,1.4fr)_minmax(0,0.8fr)_minmax(0,1.3fr)_minmax(0,1.3fr)_minmax(0,1fr)_1.75rem]"
        [ngClass]="
          shortages.has(item.id) || missingWarehouse.has(item.id)
            ? '!border-rose-200 bg-rose-50/40'
            : '!border-slate-200'
        "
        [class.opacity-70]="savingIds.has(item.id)"
      >
        <div class="flex min-w-0 items-start gap-2.5 xl:contents">
          <div class="flex min-w-0 flex-1 items-start gap-2.5 xl:order-1 xl:items-center">
            <app-warehouse-thumb [fileId]="item.variant?.photo_id" size="sm" />
            <div class="min-w-0 flex-1">
              <div class="truncate text-sm font-medium text-slate-900" [title]="getVariantTitle(item.variant)">
                {{ getVariantTitle(item.variant) || ('stock.deleted_variant' | translate) }}
              </div>
              <div class="flex flex-wrap items-center gap-x-2 text-xs text-slate-400">
                @if (getVariantCodes(item.variant)) {
                  <span>{{ getVariantCodes(item.variant) }}</span>
                }
                @if (item.variant?.is_archived) {
                  <span class="text-amber-600">{{ 'stock.archived' | translate }}</span>
                }
              </div>
            </div>
          </div>
          <div class="shrink-0 text-right xl:order-6">
            <div class="whitespace-nowrap text-sm font-semibold text-slate-900">
              {{ formatMoneyAmount(item.amount, item.currency) }}
            </div>
            @if (item.discount_type && item.amount !== getLineTotal(item)) {
              <div class="whitespace-nowrap text-xs text-slate-400 line-through">
                {{ formatMoneyAmount(getLineTotal(item), item.currency) }}
              </div>
            }
          </div>
          @if (!writtenOff) {
            <button
              type="button"
              class="-mr-1 -mt-1 grid h-7 w-7 shrink-0 place-items-center rounded text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50 xl:order-7 xl:m-0"
              [matTooltip]="'delete' | translate"
              [disabled]="savingIds.has(item.id)"
              (click)="remove(item)"
            >
              <app-icon class="!h-4 !w-4 !text-[16px] !leading-none">delete</app-icon>
            </button>
          }
        </div>

        <div
          class="grid grid-cols-2 gap-2 sm:grid-cols-[minmax(0,1.4fr)_minmax(0,0.8fr)_minmax(0,1.2fr)_minmax(0,1.2fr)] xl:contents"
        >
          <div class="col-span-2 flex min-w-0 flex-col gap-0.5 sm:col-span-1 xl:order-2">
            <span class="text-[11px] font-medium text-slate-400 xl:hidden">{{ 'warehouse' | translate }}</span>
            <app-select
              class="block"
              [ngClass]="missingWarehouse.has(item.id) ? '[&_.p-select]:!border-red-400' : ''"
              size="small"
              [options]="warehouseOptions"
              optionLabel="name"
              optionValue="id"
              [showClear]="true"
              [disabled]="writtenOff || savingIds.has(item.id)"
              [placeholder]="'stock.card_items.no_warehouse' | translate"
              [ngModel]="current(item, 'warehouse_id')"
              (ngModelChange)="change(item, { warehouse_id: $event ?? null })"
            />
          </div>
          <div class="flex min-w-0 flex-col gap-0.5 xl:order-3">
            <span class="text-[11px] font-medium text-slate-400 xl:hidden">
              {{ 'stock.quantity' | translate }}{{ item.variant?.unit ? ', ' + item.variant?.unit : '' }}
            </span>
            <span class="flex min-w-0 items-center gap-1">
              <ng-container [ngTemplateOutlet]="typedInput" [ngTemplateOutletContext]="{ item, field: 'quantity' }" />
              @if (item.variant?.unit) {
                <span class="hidden shrink-0 text-xs text-slate-400 xl:inline">{{ item.variant?.unit }}</span>
              }
            </span>
          </div>
          <div class="flex min-w-0 flex-col gap-0.5 xl:order-4">
            <span class="text-[11px] font-medium text-slate-400 xl:hidden">{{ 'price' | translate }}</span>
            <div class="flex min-w-0 items-center gap-1">
              <ng-container
                [ngTemplateOutlet]="typedInput"
                [ngTemplateOutletContext]="{ item, field: 'price', label: 'price' }"
              />
              <app-select
                class="block w-[68px] shrink-0"
                size="small"
                [options]="currencyOptions"
                optionLabel="symbol"
                optionValue="value"
                [disabled]="writtenOff || savingIds.has(item.id)"
                [ngModel]="current(item, 'currency')"
                (ngModelChange)="$event && change(item, { currency: $event })"
              />
            </div>
          </div>
          <div class="flex min-w-0 flex-col gap-0.5 xl:order-5">
            <span class="text-[11px] font-medium text-slate-400 xl:hidden">{{ 'discount' | translate }}</span>
            <div class="flex min-w-0 items-center gap-1">
              <div class="flex h-8 shrink-0 overflow-hidden rounded-md border !border-slate-200 text-xs font-semibold">
                @for (type of discountTypes; track type) {
                  <button
                    type="button"
                    class="w-7 transition first:!border-l-0 border-l !border-slate-200 disabled:cursor-not-allowed"
                    [ngClass]="
                      current(item, 'discount_type') === type
                        ? 'bg-orange-500 text-white'
                        : 'bg-white text-slate-500 hover:bg-slate-50'
                    "
                    [disabled]="writtenOff"
                    (click)="setDiscountType(item, type)"
                  >
                    {{ type === 'PERCENT' ? '%' : getCurrencySymbol(item.currency) }}
                  </button>
                }
              </div>
              <ng-container
                [ngTemplateOutlet]="typedInput"
                [ngTemplateOutletContext]="{ item, field: 'discount_value', label: 'discount' }"
              />
              @if (item.discount_type && !writtenOff) {
                <button
                  type="button"
                  class="grid h-8 w-6 shrink-0 place-items-center rounded text-slate-400 hover:text-rose-600"
                  [matTooltip]="'stock.card_items.remove_discount' | translate"
                  (click)="clearDiscount(item)"
                >
                  <app-icon class="!h-4 !w-4 !text-[16px] !leading-none">close</app-icon>
                </button>
              }
            </div>
          </div>
        </div>

        @if (item.reserved_quantity > 0 || shortages.has(item.id) || missingWarehouse.has(item.id)) {
          <div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs xl:order-8 xl:col-span-full">
            @if (item.reserved_quantity > 0) {
              <span class="inline-flex items-center gap-1 text-sky-700">
                <app-icon class="!h-3.5 !w-3.5 !text-[14px] !leading-none">lock_clock</app-icon>
                {{
                  'stock.card_items.reserved'
                    | translate: { quantity: formatQuantity(item.reserved_quantity), unit: item.variant?.unit ?? '' }
                }}
              </span>
            }
            @if (shortages.get(item.id); as shortage) {
              <span class="inline-flex items-center gap-1 font-medium text-rose-600">
                <app-icon class="!h-3.5 !w-3.5 !text-[14px] !leading-none">error</app-icon>
                {{
                  'stock.document.shortage'
                    | translate: { available: formatQuantity(shortage.available), warehouse: shortage.warehouse_name }
                }}
              </span>
            }
            @if (missingWarehouse.has(item.id)) {
              <span class="inline-flex items-center gap-1 font-medium text-rose-600">
                <app-icon class="!h-3.5 !w-3.5 !text-[14px] !leading-none">error</app-icon>
                {{ 'stock.card_items.warehouse_required' | translate }}
              </span>
            }
          </div>
        }
      </div>
    } @empty {
      <p class="m-0 rounded-lg bg-slate-50 px-3 py-4 text-center text-sm text-slate-400">
        {{ 'stock.card_items.empty' | translate }}
      </p>
    }
  </div>

  @if (!writtenOff) {
    <div class="mt-3">
      <app-warehouse-variant-picker
        [selected]="items"
        [disabled]="adding"
        [placeholder]="'stock.card_items.add_placeholder' | translate"
        (picked)="add($event)"
      />
    </div>
  }

  <div class="mt-3 flex flex-wrap items-start justify-between gap-x-6 gap-y-2 border-t !border-slate-100 pt-3">
    <div class="flex min-w-0 flex-col gap-1">
      <app-checkbox
        [checked]="pendingPriceFromItems ?? priceFromItems"
        [disabled]="pendingPriceFromItems !== null"
        [disableRipple]="true"
        (change)="togglePriceFromItems($event)"
      >
        <span class="text-sm text-slate-700">{{ 'stock.card_items.price_from_items' | translate }}</span>
      </app-checkbox>
      @if ((pendingPriceFromItems ?? priceFromItems) && hasItemsInOtherCurrency) {
        <p class="m-0 pl-8 text-xs text-amber-600">
          {{ 'stock.card_items.price_from_items_other_currency' | translate }}
        </p>
      }
    </div>
    @if (totals.length) {
      <div class="ml-auto flex flex-wrap items-center justify-end gap-x-4 gap-y-1">
        <span class="text-sm text-slate-500">{{ 'stock.card_items.total' | translate }}</span>
        @for (total of totals; track total.currency) {
          <span class="text-base font-bold text-slate-900">{{ formatMoneyAmount(total.amount, total.currency) }}</span>
        }
      </div>
    }
  </div>
</section>

<ng-template #typedInput let-item="item" let-field="field" let-label="label">
  <input
    type="text"
    inputmode="decimal"
    placeholder="0"
    class="h-8 w-full min-w-0 flex-1 rounded-md border bg-white px-2 text-right text-sm text-slate-800 outline-none focus:!border-orange-400 disabled:bg-slate-50"
    [ngClass]="invalid.get(item.id)?.has(field) ? '!border-red-400' : '!border-slate-200'"
    [disabled]="writtenOff"
    [attr.aria-label]="label ? (label | translate) : null"
    [ngModel]="getText(item, field)"
    (ngModelChange)="onInput(item, field, $event)"
    (blur)="commit(item, field)"
    (keydown.enter)="commit(item, field)"
  />
</ng-template>
